import { DefaultBalanceModule, Balance, Balances } from '@talismn/balances';
import { githubTokenLogoUrl } from '@talismn/chaindata-provider';
import { isEthereumAddress, hasOwnProperty } from '@talismn/util';
import isEqual from 'lodash/isEqual';
import { parseAbi } from 'viem';
import anylogger from 'anylogger';

const abiMulticall = parseAbi(["struct Call { address target; bytes callData; }", "struct Call3 { address target; bool allowFailure; bytes callData; }", "struct Call3Value { address target; bool allowFailure; uint256 value; bytes callData; }", "struct Result { bool success; bytes returnData; }", "function aggregate(Call[] calldata calls) public payable returns (uint256 blockNumber, bytes[] memory returnData)", "function aggregate3(Call3[] calldata calls) public payable returns (Result[] memory returnData)", "function aggregate3Value(Call3Value[] calldata calls) public payable returns (Result[] memory returnData)", "function blockAndAggregate(Call[] calldata calls) public payable returns (uint256 blockNumber, bytes32 blockHash, Result[] memory returnData)", "function getBasefee() view returns (uint256 basefee)", "function getBlockHash(uint256 blockNumber) view returns (bytes32 blockHash)", "function getBlockNumber() view returns (uint256 blockNumber)", "function getChainId() view returns (uint256 chainid)", "function getCurrentBlockCoinbase() view returns (address coinbase)", "function getCurrentBlockDifficulty() view returns (uint256 difficulty)", "function getCurrentBlockGasLimit() view returns (uint256 gaslimit)", "function getCurrentBlockTimestamp() view returns (uint256 timestamp)", "function getEthBalance(address addr) view returns (uint256 balance)", "function getLastBlockHash() view returns (bytes32 blockHash)", "function tryAggregate(bool requireSuccess, Call[] calldata calls) public payable returns (Result[] memory returnData)", "function tryBlockAndAggregate(bool requireSuccess, Call[] calldata calls) public payable returns (uint256 blockNumber, bytes32 blockHash, Result[] memory returnData)"]);

var packageJson = {
	name: "@talismn/balances-evm-native",
	version: "0.0.0-pr1199-20231219213830",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "GPL-3.0-or-later",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/balances-evm-native",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-balances-evm-native.cjs.js",
	module: "dist/talismn-balances-evm-native.esm.js",
	files: [
		"/dist"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist && rm -rf .turbo rm -rf node_modules"
	},
	dependencies: {
		"@talismn/balances": "workspace:*",
		"@talismn/chaindata-provider": "workspace:*",
		"@talismn/util": "workspace:*",
		anylogger: "^1.0.11",
		lodash: "4.17.21",
		viem: "^1.18.9"
	},
	devDependencies: {
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		"@types/lodash": "^4.14.180",
		eslint: "^8.52.0",
		jest: "^28.1.0",
		"ts-jest": "^28.0.2",
		typescript: "^5.2.2"
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

const evmNativeTokenId = (chainId, tokenSymbol) => `${chainId}-evm-native-${tokenSymbol}`.toLowerCase().replace(/ /g, "-");
const EvmNativeModule = hydrate => {
  const {
    chainConnectors,
    chaindataProvider
  } = hydrate;
  return {
    ...DefaultBalanceModule("evm-native"),
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/updateEvmNetworksFromGithub.ts#L280-L284).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the chainmeta for each chain up to date without relying on a squid.
     */
    async fetchEvmChainMeta(chainId) {
      const isTestnet = (await chaindataProvider.getEvmNetwork(chainId))?.isTestnet || false;
      return {
        isTestnet
      };
    },
    /**
     * This method is currently executed on [a squid](https://github.com/TalismanSociety/chaindata-squid/blob/0ee02818bf5caa7362e3f3664e55ef05ec8df078/src/steps/updateEvmNetworksFromGithub.ts#L338-L343).
     * In a future version of the balance libraries, we may build some kind of async scheduling system which will keep the list of tokens for each chain up to date without relying on a squid.
     */
    async fetchEvmChainTokens(chainId, chainMeta, moduleConfig) {
      const {
        isTestnet
      } = chainMeta;
      const symbol = moduleConfig?.symbol || "ETH";
      const decimals = typeof moduleConfig?.decimals === "number" ? moduleConfig.decimals : 18;
      const coingeckoId = typeof moduleConfig?.coingeckoId === "string" ? moduleConfig.coingeckoId : undefined;
      const id = evmNativeTokenId(chainId, symbol);
      const nativeToken = {
        id,
        type: "evm-native",
        isTestnet,
        symbol,
        decimals,
        logo: githubTokenLogoUrl(id),
        coingeckoId,
        evmNetwork: {
          id: chainId
        }
      };
      return {
        [nativeToken.id]: nativeToken
      };
    },
    async subscribeBalances(addressesByToken, callback) {
      let subscriptionActive = true;
      const subscriptionInterval = 6_000; // 6_000ms == 6 seconds
      const cache = new Map();

      // for chains with a zero balance we only call fetchBalances once every 5 subscriptionIntervals
      // if subscriptionInterval is 6 seconds, this means we only poll chains with a zero balance every 30 seconds
      let zeroBalanceSubscriptionIntervalCounter = 0;
      const poll = async () => {
        if (!subscriptionActive) return;
        zeroBalanceSubscriptionIntervalCounter = (zeroBalanceSubscriptionIntervalCounter + 1) % 5;
        try {
          // fetch balance for each network sequentially to prevent creating a big queue of http requests (browser can only handle 2 at a time)
          // since these are native tokens (1 per network), we can iterate on tokens
          for (const tokenId of Object.keys(addressesByToken)) {
            const cached = cache.get(tokenId);
            if (cached && zeroBalanceSubscriptionIntervalCounter !== 0 && new Balances(cached).each.reduce((sum, b) => sum + b.total.planck, 0n) === 0n) {
              // only poll empty token balances every 5 subscriptionIntervals
              continue;
            }
            try {
              const tokenAddresses = {
                [tokenId]: addressesByToken[tokenId]
              };
              const balances = await this.fetchBalances(tokenAddresses);

              // Don't call callback with balances which have not changed since the last poll.
              const json = balances.toJSON();
              if (!isEqual(cache.get(tokenId), json)) {
                cache.set(tokenId, json);
                callback(null, balances);
              }
            } catch (err) {
              callback(err);
            }
          }
        } catch (error) {
          callback(error);
        } finally {
          setTimeout(poll, subscriptionInterval);
        }
      };
      setTimeout(poll, subscriptionInterval);
      return () => {
        subscriptionActive = false;
      };
    },
    async fetchBalances(addressesByToken) {
      const evmNetworks = await chaindataProvider.evmNetworks();
      const tokens = await chaindataProvider.tokens();
      const balances = (await Promise.allSettled(Object.entries(addressesByToken).map(async ([tokenId, addresses]) => {
        if (!chainConnectors.evm) throw new Error(`This module requires an evm chain connector`);
        const token = tokens[tokenId];
        if (!token) throw new Error(`Token ${tokenId} not found`);

        // TODO: Fix @talismn/balances-react: it shouldn't pass every token to every module
        if (token.type !== "evm-native") throw new Error(`This module doesn't handle tokens of type ${token.type}`);
        const evmNetworkId = token.evmNetwork?.id;
        if (!evmNetworkId) throw new Error(`Token ${tokenId} has no evm network`);
        const evmNetwork = evmNetworks[evmNetworkId];
        if (!evmNetwork) throw new Error(`Evm network ${evmNetworkId} not found`);
        const publicClient = await chainConnectors.evm.getPublicClientForEvmNetwork(evmNetworkId);
        if (!publicClient) throw new Error(`Could not get rpc provider for evm network ${evmNetworkId}`);

        // fetch all balances
        const freeBalances = await getFreeBalances(publicClient, addresses);
        const balanceResults = addresses.map((address, i) => {
          if (freeBalances[i] === "error") return false;
          return new Balance({
            source: "evm-native",
            status: "live",
            address: address,
            multiChainId: {
              evmChainId: evmNetwork.id
            },
            evmNetworkId,
            tokenId,
            free: freeBalances[i].toString()
          });
        }).filter(balance => balance !== false);

        // return to caller
        return new Balances(balanceResults);
      }))).map(result => {
        if (result.status === "rejected") {
          log.debug(result.reason);
          return false;
        }
        return result.value;
      }).filter(balances => balances !== false);
      return balances.reduce((allBalances, balances) => allBalances.add(balances), new Balances([]));
    }
  };
};
async function getFreeBalance(publicClient, address) {
  if (!isEthereumAddress(address)) return 0n;
  try {
    return await publicClient.getBalance({
      address
    });
  } catch (error) {
    const errorMessage = hasOwnProperty(error, "shortMessage") ? error.shortMessage : hasOwnProperty(error, "message") ? error.message : error;
    log.warn(`Failed to get balance from chain ${publicClient.chain?.id} for address ${address}: ${errorMessage}`);
    return "error";
  }
}
async function getFreeBalances(publicClient, addresses) {
  // if multicall is available, use it to save RPC rate limits
  if (publicClient.batch?.multicall && publicClient.chain?.contracts?.multicall3?.address) {
    try {
      const ethAddresses = addresses.filter(isEthereumAddress);
      const addressMulticall = publicClient.chain.contracts.multicall3.address;
      const callResults = await publicClient.multicall({
        contracts: ethAddresses.map(address => ({
          address: addressMulticall,
          abi: abiMulticall,
          functionName: "getEthBalance",
          args: [address]
        }))
      });
      const ethBalanceResults = Object.fromEntries(ethAddresses.map((address, i) => {
        const {
          error
        } = callResults[i];
        if (error) {
          const errorMessage = hasOwnProperty(error, "shortMessage") ? error.shortMessage : hasOwnProperty(error, "message") ? error.message : error;
          log.warn(`Failed to get balance from chain ${publicClient.chain?.id} for address ${address}: ${errorMessage}`);
        }
        return [address, callResults[i].result ?? "error"];
      }));

      // default to 0 for non evm addresses
      return addresses.map(address => ethBalanceResults[address] ?? 0n);
    } catch (err) {
      const errorMessage = hasOwnProperty(err, "shortMessage") ? err.shortMessage : hasOwnProperty(err, "message") ? err.message : err;
      log.warn(`Failed to get balance from chain ${publicClient.chain?.id} for ${addresses.length} addresses: ${errorMessage}`);
      return addresses.map(() => "error");
    }
  }
  return Promise.all(addresses.map(address => getFreeBalance(publicClient, address)));
}

export { EvmNativeModule, evmNativeTokenId };
